// The inspector while the code map is in view: a member (what it calls, what calls it), a file,
// a line (each call site), how the map was read, or the endpoint or test the tab in front shows.
import { endpointReach, endpointTestedBy, handledBy, testedBy, testReach, TOP_LEVEL_MAIN, type CodeCall, type CodeEndpoint, type CodeHandoff, type CodeMap, type CodeMember, type CodeMemberKind, type CodeRequest, type CodeTest, type Reach } from '@modelviewer/core';
import { ArrowRight, Database, FileCode2, FlaskConical, Globe, History, LogOut, TriangleAlert, VenetianMask } from 'lucide-react';
import { useMemo } from 'react';
import { cn } from '../lib/cn';
import { openEndpointMap, openFiles, openSourceFile, openTestMap } from '../state/dock';
import { revealFile } from '../state/files';
import { revealCode, selectCode, showEndpoint, showTest, useCodeMap } from '../state/codemap';
import { Field, Mono, Section, SourceLink } from '../views/InspectorParts';
import { codeIndex, idLabel, implementationsOf, memberLabel, type CodeIndex } from './codeIndex';
import { endpointLabel } from './endpointGraph';
import { MethodBadges, useCurrentEndpoint } from './EndpointMapView';
import { testFilesOf } from './graph';
import { useCodeSettings } from './settings';
import { FRAMEWORK_NAMES, testKind, typeName } from './testGraph';
import { useCurrentTest } from './TestMapView';

const KIND: Record<CodeMemberKind, string> = {
  method: 'Method',
  constructor: 'Constructor',
  destructor: 'Destructor',
  operator: 'Operator',
  conversion: 'Conversion',
  property: 'Property',
  indexer: 'Indexer',
  event: 'Event',
  field: 'Field',
  const: 'Constant',
  enumMember: 'Enum member',
};

const fileName = (path: string) => path.slice(path.lastIndexOf('/') + 1);

/** Calls grouped by the member (or type) on the other end, in the order they first appear. */
function grouped(calls: CodeCall[], end: 'from' | 'to'): { id: string; calls: CodeCall[]; exact: boolean }[] {
  const out = new Map<string, { id: string; calls: CodeCall[]; exact: boolean }>();
  for (const c of [...calls].sort((a, b) => (a.file === b.file ? a.line - b.line : a.file < b.file ? -1 : 1))) {
    const id = c[end];
    const g = out.get(id) ?? { id, calls: [], exact: false };
    g.calls.push(c);
    g.exact ||= c.certainty === 'exact';
    out.set(id, g);
  }
  return [...out.values()];
}

function Empty({ children }: { children: React.ReactNode }) {
  return <p className="text-[12px] leading-relaxed text-faint">{children}</p>;
}

/** A member or type in a list: selects it on the map. */
function Target({ id, index, calls, exact }: { id: string; index: CodeIndex; calls?: CodeCall[]; exact?: boolean }) {
  const file = index.fileOf(id);
  return (
    <button
      type="button"
      onClick={() => file && revealCode(file, index.members.has(id) ? { member: id } : {})}
      className="flex w-full items-center gap-2 rounded-md px-1.5 py-1 text-left text-[12.5px] hover:bg-hover"
      title={file ? `${file}:${index.lineOf(id)}` : id}
    >
      <span className="min-w-0 flex-1 truncate font-mono text-[12px]">{idLabel(id, index)}</span>
      {exact === false && <span className="mv-tag flex-none">guess</span>}
      {calls && calls.length > 1 && <span className="flex-none text-[11px] text-faint">×{calls.length}</span>}
      {file && <span className="max-w-[40%] flex-none truncate text-[11px] text-faint">{fileName(file)}</span>}
    </button>
  );
}

function CallList({ calls, end, index, empty }: { calls: CodeCall[]; end: 'from' | 'to'; index: CodeIndex; empty: string }) {
  const groups = useMemo(() => grouped(calls, end), [calls, end]);
  if (!groups.length) return <Empty>{empty}</Empty>;
  return (
    <div className="-mx-1.5">
      {groups.map((g) => (
        <Target key={g.id} id={g.id} index={index} calls={g.calls} exact={g.exact} />
      ))}
    </div>
  );
}

/**
 * Where code leaves for the framework or packages, by what it is handed to, with the code's
 * probable handlers of what is handed over (a guess by shape, followed with guessed calls).
 */
function HandoffList({ handoffs, index, testId }: { handoffs: CodeHandoff[]; index: CodeIndex; testId: string }) {
  const groups = useMemo(() => {
    const out = new Map<string, { to: string; types: Set<string>; sites: CodeHandoff[] }>();
    for (const h of handoffs) {
      const g = out.get(h.to) ?? { to: h.to, types: new Set<string>(), sites: [] };
      for (const t of h.types) g.types.add(t);
      g.sites.push(h);
      out.set(h.to, g);
    }
    return [...out.values()].sort((a, b) => b.sites.length - a.sites.length || (a.to < b.to ? -1 : 1));
  }, [handoffs]);
  if (!groups.length) return <Empty>Hands nothing of its own to the framework or packages.</Empty>;
  return (
    <div className="-mx-1.5 space-y-1" data-testid={testId}>
      {groups.map((g) => {
        const handlers = index.map.handlers.filter((h) => g.types.has(h.type));
        const first = g.sites[0]!;
        return (
          <div key={g.to}>
            <button type="button" onClick={() => openSourceFile(first.file, first.line)} className="flex w-full items-center gap-2 rounded-md px-1.5 py-1 text-left text-[12.5px] hover:bg-hover" title={`${first.file}:${first.line}`}>
              <LogOut size={13} className="flex-none text-faint" />
              <span className="min-w-0 flex-1 truncate">
                <span className="font-mono text-[12px]">{g.to}</span> <span className="text-faint">gets</span> {[...g.types].map((t) => index.types.get(t)?.name ?? t).join(', ')}
              </span>
              {g.sites.length > 1 && <span className="flex-none text-[11px] text-faint">×{g.sites.length}</span>}
            </button>
            {handlers.map((h) => (
              <button
                key={h.member}
                type="button"
                onClick={() => {
                  const file = index.fileOf(h.member);
                  if (file) revealCode(file, { member: h.member });
                }}
                className="flex w-full items-center gap-2 rounded-md py-0.5 pr-1.5 pl-7 text-left text-[12px] text-muted hover:bg-hover hover:text-text"
                title={`${h.via}: it takes a ${index.types.get(h.type)?.name ?? h.type}`}
              >
                <span className="flex-none text-faint">probably handled by</span>
                <span className="min-w-0 flex-1 truncate font-mono">{idLabel(h.member, index)}</span>
                <span className="mv-tag flex-none">guess</span>
              </button>
            ))}
          </div>
        );
      })}
    </div>
  );
}

/** HTTP requests the code sends, each with the endpoint of the code it reaches (or none). */
function RequestList({ requests, testId }: { requests: CodeRequest[]; testId: string }) {
  if (!requests.length) return <Empty>Sends no HTTP request the code map can read.</Empty>;
  return (
    <div className="-mx-1.5" data-testid={testId}>
      {requests.map((r, i) => (
        <div key={`${r.file}:${r.line}:${i}`} className="rounded-md px-1.5 py-1">
          <button type="button" onClick={() => openSourceFile(r.file, r.line)} className="flex w-full items-center gap-2 text-left text-[12.5px] hover:text-accent" title={`${r.file}:${r.line}`}>
            <Globe size={13} className="flex-none text-faint" />
            <span className="min-w-0 flex-1 truncate font-mono text-[12px]">
              {r.method} {r.url}
            </span>
          </button>
          {r.endpoints.length ? (
            r.endpoints.map((id) => (
              <button
                key={id}
                type="button"
                onClick={() => {
                  showEndpoint(id);
                  openEndpointMap();
                }}
                className="flex w-full items-center gap-2 py-0.5 pl-5.5 text-left text-[12px] text-muted hover:text-text"
              >
                <span className="flex-none text-faint">reaches</span>
                <span className="min-w-0 flex-1 truncate font-mono">{id}</span>
                {r.certainty === 'guess' && <span className="mv-tag flex-none">guess</span>}
              </button>
            ))
          ) : (
            <p className="pl-5.5 text-[12px] text-faint">No endpoint of the code takes this method and route.</p>
          )}
        </div>
      ))}
    </div>
  );
}

/** Test files the code map in front leaves out: tests that reach a member are under "Tested by" instead. */
function useHiddenTestFiles(index: CodeIndex): Set<string> {
  const hide = useCodeSettings((s) => s.tests === 'hide');
  const view = useCodeMap((s) => s.view);
  return useMemo(() => (hide && view === 'code' ? testFilesOf(index.map) : new Set<string>()), [hide, view, index]);
}

/** Opens a test on the test map. */
function openTest(id: string): void {
  showTest(id);
  openTestMap();
}

function TestedByList({ reaching, index }: { reaching: { test: string; depth: number; via?: 'request' | 'handler' }[]; index: CodeIndex }) {
  const tests = useMemo(() => new Map(index.map.tests.map((t) => [t.id, t])), [index]);
  if (!reaching.length) return <Empty>No test reaches it, as far as the code map can tell.</Empty>;
  return (
    <div className="-mx-1.5" data-testid="tested-by">
      {reaching.map(({ test: id, depth, via }) => {
        const t = tests.get(id);
        if (!t) return null;
        return (
          <button key={id} type="button" onClick={() => openTest(id)} title={`${typeName(t.type)}.${t.name}\n${t.file}:${t.line}`} className="flex w-full items-center gap-2 rounded-md px-1.5 py-1 text-left text-[12.5px] hover:bg-hover">
            <FlaskConical size={13} className="flex-none text-faint" />
            <span className={cn('min-w-0 flex-1 truncate font-mono text-[12px]', t.skipped && 'line-through decoration-faint')}>{t.name}</span>
            <span className="flex-none text-[11px] text-faint">{depth === 0 ? 'runs with it' : via === 'request' ? (depth === 1 ? 'sends it a request' : `sends it a request, ${depth} steps away`) : depth === 1 ? 'calls it' : `${depth} steps away`}</span>
          </button>
        );
      })}
    </div>
  );
}

function MemberDetails({ member, index }: { member: CodeMember; index: CodeIndex }) {
  const type = index.types.get(member.type);
  const hidden = useHiddenTestFiles(index);
  const guesses = useCodeSettings((s) => s.guesses);
  const out = index.callsFrom.get(member.id) ?? [];
  const into = index.callsTo.get(member.id) ?? [];
  // Setting up or checking a mock is not a call; calls from hidden test files are under "Tested by".
  const shown = (c: CodeCall, end: 'from' | 'to') => !c.mock && !hidden.has(index.fileOf(c[end]) ?? '');
  const calls = out.filter((c) => c.kind !== 'access' && shown(c, 'to'));
  const callers = into.filter((c) => c.kind !== 'access' && shown(c, 'from'));
  const reads = out.filter((c) => c.kind === 'access' && shown(c, 'to'));
  const readers = into.filter((c) => c.kind === 'access' && shown(c, 'from'));
  const implementations = useMemo(() => implementationsOf(member, index), [member, index]);
  const hasTests = index.map.tests.length > 0;
  const reaching = useMemo(() => (hasTests ? (testedBy(index.map, { guesses }).get(member.id) ?? []) : []), [hasTests, index, member, guesses]);
  const asTest = index.map.tests.find((t) => t.id === member.id);
  const onlyTestsCall = hidden.size > 0 && reaching.some((r) => r.depth === 1);
  const handoffs = useMemo(() => index.map.handoffs.filter((h) => h.from === member.id), [index, member]);
  const requests = useMemo(() => index.map.requests.filter((r) => r.from === member.id), [index, member]);
  // As a handler: where what it takes is handed to a package.
  const handedFrom = useMemo(() => handledBy(index.map).filter((h) => h.to === member.id), [index, member]);
  const isValue = member.kind === 'property' || member.kind === 'field' || member.kind === 'const' || member.kind === 'event' || member.kind === 'enumMember';
  return (
    <>
      <header className="space-y-1.5 border-b border-border px-3 pt-3 pb-3">
        <p className="text-[11px] font-semibold tracking-wider text-faint uppercase">
          {member.name === TOP_LEVEL_MAIN ? 'Top-level statements' : KIND[member.kind]} in {type?.name ?? member.type}
        </p>
        <h2 className="font-mono text-[13px] font-semibold break-words" data-testid="code-member-title">
          {memberLabel(member, index, false)}
        </h2>
        {member.valueType && member.kind !== 'constructor' && member.name !== TOP_LEVEL_MAIN && (
          <Field label={isValue ? 'Type' : 'Returns'}>
            <Mono>{member.valueType}</Mono>
          </Field>
        )}
        {member.modifiers.length > 0 && <Field label="Modifiers">{member.modifiers.join(' ')}</Field>}
        {!member.body && !isValue && <Field label="Body">none (abstract or interface member)</Field>}
        <SourceLink file={member.file} line={member.line} />
        {asTest && (
          <button type="button" onClick={() => openTest(asTest.id)} className="flex items-center gap-1.5 text-[12px] text-accent hover:underline" data-testid="open-test">
            <FlaskConical size={12} /> A test: show what it reaches
          </button>
        )}
      </header>
      {!isValue && (
        <Section title="Calls" count={calls.length}>
          <CallList calls={calls} end="to" index={index} empty="Calls nothing declared in this folder." />
        </Section>
      )}
      <Section title={isValue ? 'Used by' : 'Called by'} count={isValue ? readers.length : callers.length}>
        <CallList
          calls={isValue ? readers : callers}
          end="from"
          index={index}
          empty={onlyTestsCall ? 'Only tests call it (test files are hidden on the code map).' : isValue ? 'Nothing in this folder reads or writes it.' : 'Nothing in this folder calls it, as far as the code map can tell.'}
        />
      </Section>
      {hasTests && !asTest && (
        <Section title="Tested by" count={reaching.length}>
          <TestedByList reaching={reaching} index={index} />
        </Section>
      )}
      {requests.length > 0 && (
        <Section title="HTTP requests" count={requests.length}>
          <RequestList requests={requests} testId="member-requests" />
        </Section>
      )}
      {handoffs.length > 0 && (
        <Section title="Leaves the code" count={handoffs.length}>
          <HandoffList handoffs={handoffs} index={index} testId="member-handoffs" />
        </Section>
      )}
      {handedFrom.length > 0 && (
        <Section title="Probably handed from" count={handedFrom.length}>
          <div className="-mx-1.5" data-testid="handed-from">
            {handedFrom.map((h) => (
              <button key={`${h.from}|${h.line}`} type="button" onClick={() => openSourceFile(h.file, h.line)} className="flex w-full items-center gap-2 rounded-md px-1.5 py-1 text-left text-[12.5px] hover:bg-hover" title={`${h.file}:${h.line}`}>
                <span className="min-w-0 flex-1 truncate font-mono text-[12px]">{index.members.has(h.from) ? idLabel(h.from, index) : h.from}</span>
                <span className="flex-none text-[11px] text-faint">through {h.via}</span>
                <span className="mv-tag flex-none">guess</span>
              </button>
            ))}
          </div>
          <p className="mt-1.5 text-[12px] leading-relaxed text-faint">A guess: it takes a {index.types.get(handedFrom[0]!.type)?.name ?? handedFrom[0]!.type}, and its class implements a package type made for it. Something there finds it at run time.</p>
        </Section>
      )}
      {implementations.length > 0 && (
        <Section title="Implemented by" count={implementations.length}>
          <div className="-mx-1.5">
            {implementations.map((m) => (
              <Target key={m.id} id={m.id} index={index} />
            ))}
          </div>
        </Section>
      )}
      {!isValue && reads.length > 0 && (
        <Section title="Reads and writes" count={reads.length} defaultOpen={false}>
          <CallList calls={reads} end="to" index={index} empty="" />
        </Section>
      )}
    </>
  );
}

/** Files on the other end of a file's calls or uses, with counts. */
function FileList({ counts, empty }: { counts: Map<string, number>; empty: string }) {
  if (!counts.size) return <Empty>{empty}</Empty>;
  return (
    <div className="-mx-1.5">
      {[...counts]
        .sort((a, b) => b[1] - a[1] || (a[0] < b[0] ? -1 : 1))
        .map(([path, n]) => (
          <button key={path} type="button" onClick={() => revealCode(path)} className="flex w-full items-center gap-2 rounded-md px-1.5 py-1 text-left text-[12.5px] hover:bg-hover" title={path}>
            <FileCode2 size={13} className="flex-none text-faint" />
            <span className="min-w-0 flex-1 truncate">{fileName(path)}</span>
            <span className="flex-none text-[11px] text-faint">{n}</span>
          </button>
        ))}
    </div>
  );
}

function FileDetails({ path, index }: { path: string; index: CodeIndex }) {
  const file = index.map.files.find((f) => f.path === path);
  const hidden = useHiddenTestFiles(index);
  const { callsInto, calledFrom, usesFrom, usedBy } = useMemo(() => {
    const count = (m: Map<string, number>, key: string | null, n = 1) => key && key !== path && !hidden.has(key) && m.set(key, (m.get(key) ?? 0) + n);
    const callsInto = new Map<string, number>();
    const calledFrom = new Map<string, number>();
    const usesFrom = new Map<string, number>();
    const usedBy = new Map<string, number>();
    for (const c of index.map.calls) {
      if (c.kind === 'access' || c.mock) continue;
      if (index.fileOf(c.from) === path) count(callsInto, index.fileOf(c.to));
      if (index.fileOf(c.to) === path) count(calledFrom, index.fileOf(c.from));
    }
    for (const u of index.map.uses) {
      if (index.fileOf(u.from) === path) count(usesFrom, index.fileOf(u.to), u.count);
      if (index.fileOf(u.to) === path) count(usedBy, index.fileOf(u.from), u.count);
    }
    return { callsInto, calledFrom, usesFrom, usedBy };
  }, [path, index, hidden]);
  if (!file) return <Empty>This file is no longer on the code map.</Empty>;
  return (
    <>
      <header className="space-y-1.5 border-b border-border px-3 pt-3 pb-3">
        <p className="text-[11px] font-semibold tracking-wider text-faint uppercase">File</p>
        <h2 className="text-[14px] font-semibold break-words" data-testid="code-file-title">
          {fileName(path)}
        </h2>
        <Field label="Folder">{path.includes('/') ? path.slice(0, path.lastIndexOf('/')) : '(root)'}</Field>
        {file.namespace && <Field label="Namespace">{file.namespace}</Field>}
        {file.project !== null && <Field label="Project">{file.project || '(root)'}</Field>}
        <Field label="Lines">{file.lines}</Field>
        {file.problems > 0 && (
          <p className="flex gap-1.5 text-[12px] text-changed">
            <TriangleAlert size={13} className="mt-0.5 flex-none" />
            {file.problems} statement{file.problems > 1 ? 's' : ''} could not be read; calls in {file.problems > 1 ? 'them are' : 'it are'} guessed by name.
          </p>
        )}
        <div className="flex flex-wrap gap-x-4 gap-y-1">
          <button onClick={() => openSourceFile(path)} className="flex items-center gap-1.5 text-[12px] text-accent hover:underline">
            <FileCode2 size={13} /> Open the source
          </button>
          <button
            onClick={() => {
              void revealFile(path, { history: true });
              openFiles();
            }}
            className="flex items-center gap-1.5 text-[12px] text-accent hover:underline"
          >
            <History size={13} /> Its history
          </button>
        </div>
      </header>
      <Section title="Types" count={file.types.length}>
        <div className="-mx-1.5">
          {file.types.map((id) => {
            const t = index.types.get(id)!;
            const count = (index.membersOf.get(id) ?? []).filter((m) => m.file === path).length;
            return (
              <button key={id} type="button" onClick={() => openSourceFile(path, t.parts.find((p) => p.file === path)?.line)} className="flex w-full items-center gap-2 rounded-md px-1.5 py-1 text-left text-[12.5px] hover:bg-hover">
                <span className="w-16 flex-none text-[11px] text-faint">{t.kind}</span>
                <span className="min-w-0 flex-1 truncate font-medium">{t.typeParams.length ? `${t.name}<${t.typeParams.join(', ')}>` : t.name}</span>
                <span className="flex-none text-[11px] text-faint">{count} members</span>
              </button>
            );
          })}
        </div>
      </Section>
      <Section title="Calls into" count={callsInto.size}>
        <FileList counts={callsInto} empty="Calls nothing in other files." />
      </Section>
      <Section title="Called from" count={calledFrom.size}>
        <FileList counts={calledFrom} empty="Nothing in other files calls it." />
      </Section>
      <Section title="Uses types from" count={usesFrom.size} defaultOpen={false}>
        <FileList counts={usesFrom} empty="Uses no types of other files." />
      </Section>
      <Section title="Its types are used by" count={usedBy.size} defaultOpen={false}>
        <FileList counts={usedBy} empty="No other file uses its types." />
      </Section>
    </>
  );
}

function EdgeDetails({ id, index }: { id: string; index: CodeIndex }) {
  const kind = id.slice(0, id.indexOf(':'));
  const [from, to] = id.slice(kind.length + 1).split('->') as [string, string];
  const sites = useMemo(() => {
    if (kind !== 'call') return [];
    const own = (index.callsFrom.get(from) ?? []).filter((c) => c.to === to);
    // A line from an endpoint: the calls its handler lambda makes.
    return own.length ? own : index.map.calls.filter((c) => c.endpoint === from && c.to === to);
  }, [kind, from, to, index]);
  const uses = useMemo(() => (kind === 'uses' ? index.map.uses.filter((u) => index.fileOf(u.from) === from && index.fileOf(u.to) === to) : []), [kind, from, to, index]);
  const inherits = useMemo(
    () => (kind === 'inh' ? index.map.types.filter((t) => t.parts[0]?.file === from).flatMap((t) => [...(t.base ? [t.base] : []), ...t.interfaces].filter((s) => index.fileOf(s) === to).map((s) => ({ t, s }))) : []),
    [kind, from, to, index],
  );
  const handed = useMemo(() => (kind === 'handled' ? handledBy(index.map).filter((h) => h.to === to && (h.from === from || index.map.handoffs.some((x) => x.from === from && x.file === h.file && x.line === h.line))) : []), [kind, from, to, index]);
  const requested = useMemo(() => (kind === 'request' ? index.map.requests.filter((r) => r.from === from && r.endpoints.includes(to)) : []), [kind, from, to, index]);
  const title = kind === 'call' ? 'Call' : kind === 'uses' ? 'Uses' : kind === 'impl' ? 'Implementation' : kind === 'handled' ? 'Probably handled by' : kind === 'request' ? 'HTTP request' : 'Inherits';
  const byId = kind === 'call' || kind === 'impl' || kind === 'handled' || kind === 'request';
  return (
    <>
      <header className="space-y-1.5 border-b border-border px-3 pt-3 pb-3">
        <p className="text-[11px] font-semibold tracking-wider text-faint uppercase">{title}</p>
        <h2 className="flex flex-wrap items-center gap-1.5 font-mono text-[12.5px] font-semibold break-all">
          {byId ? idLabel(from, index) : fileName(from)} <ArrowRight size={13} className="text-faint" /> {byId ? idLabel(to, index) : fileName(to)}
        </h2>
        {kind === 'impl' && <p className="text-[12px] leading-relaxed text-muted">A call to the first can run the second: it implements or overrides it. The endpoint map follows every implementation in the folder.</p>}
        {kind === 'handled' && handed[0] && (
          <p className="text-[12px] leading-relaxed text-muted">
            A guess: the first hands a {index.types.get(handed[0].type)?.name ?? handed[0].type} to <span className="font-mono">{handed[0].via}</span>, outside the code. The second takes one, and its class implements a package type made for it, so whatever is behind {handed[0].via} probably finds it at run time.
          </p>
        )}
        {kind === 'call' && sites.some((s) => s.certainty === 'guess') && !sites.some((s) => s.certainty === 'exact') && (
          <p className="text-[12px] leading-relaxed text-muted">A guess: the type of what it is called on is unknown, and this is the only member with that name and number of arguments.</p>
        )}
      </header>
      {kind === 'call' && (
        <Section title="Where" count={sites.length}>
          <div className="space-y-0.5">
            {sites.map((c, i) => (
              <div key={i} className="flex items-center gap-2">
                <SourceLink file={c.file} line={c.line} />
                <span className={cn('text-[11px]', c.certainty === 'guess' ? 'text-changed' : 'text-faint')}>{c.kind === 'new' ? 'creates' : c.kind === 'access' ? 'reads or writes' : 'calls'}</span>
              </div>
            ))}
          </div>
        </Section>
      )}
      {kind === 'request' && <RequestList requests={requested} testId="edge-requests" />}
      {kind === 'handled' && (
        <Section title="Where" count={handed.length}>
          <div className="space-y-0.5">
            {handed.map((h, i) => (
              <div key={i} className="flex items-center gap-2">
                <SourceLink file={h.file} line={h.line} />
                <span className="text-[11px] text-changed">handed to {h.via}</span>
              </div>
            ))}
          </div>
        </Section>
      )}
      {kind === 'uses' && (
        <Section title="Uses" count={uses.length}>
          <div className="space-y-1">
            {uses.map((u, i) => (
              <div key={i} className="text-[12px]">
                <span className="font-mono">{idLabel(u.from, index)}</span> <span className="text-faint">{u.kind === 'new' ? 'creates' : u.kind === 'static' ? 'uses static members of' : u.kind === 'attribute' ? 'has the attribute' : u.kind === 'mock' ? 'fakes' : 'uses'}</span>{' '}
                <span className="font-mono">{idLabel(u.to, index)}</span>
                <SourceLink file={u.file} line={u.line} />
              </div>
            ))}
          </div>
        </Section>
      )}
      {kind === 'inh' && (
        <Section title="Types" count={inherits.length}>
          {inherits.map(({ t, s }) => (
            <p key={`${t.id}|${s}`} className="text-[12.5px]">
              <span className="font-medium">{t.name}</span> <span className="text-faint">{index.types.get(s)?.kind === 'interface' ? 'implements' : 'derives from'}</span> <span className="font-medium">{index.types.get(s)?.name}</span>
            </p>
          ))}
        </Section>
      )}
    </>
  );
}

function Summary({ map }: { map: CodeMap }) {
  const s = map.stats;
  const problems = map.files.filter((f) => f.problems > 0);
  const testFiles = map.files.filter((f) => f.test).length;
  const hideTests = useCodeSettings((st) => st.tests === 'hide');
  return (
    <>
      <header className="space-y-1.5 border-b border-border px-3 pt-3 pb-3">
        <p className="text-[11px] font-semibold tracking-wider text-faint uppercase">Code map</p>
        <Field label="Files">{s.files}</Field>
        <Field label="Types">{s.types}</Field>
        <Field label="Members">{s.members}</Field>
        <Field label="Calls">
          {s.exact} exact{s.guess > 0 && `, ${s.guess} guessed`}
        </Field>
        <Field label="Not drawn">
          {s.external} into the framework or packages{s.unresolved > 0 && `, ${s.unresolved} not found`}
        </Field>
        {testFiles > 0 && (
          <Field label="Tests">
            {map.tests.length} in {testFiles} test {testFiles === 1 ? 'file' : 'files'}
            {hideTests && <span className="text-faint"> (hidden on the map)</span>}
          </Field>
        )}
      </header>
      <Section title="How it is read">
        <div className="space-y-2 text-[12px] leading-relaxed text-muted">
          <p>The code is read in this browser, without compiling it. For each call the code map works out the type of what it is called on, then finds the member on that type, its base types or its interfaces, or an extension method for it.</p>
          <p>When that type cannot be known, the call is guessed by name: only when exactly one member in the folder has that name and number of arguments. Guessed lines are dashed.</p>
          <p>Generated files (migrations, model snapshots, *.Designer.cs, *.g.cs), build output and hidden folders are left out.</p>
          <p>Test files (in a test project, or declaring tests) are hidden at first: the test files menu dims or shows them. Each member row counts the tests that reach it, as the test map follows them. Setting up or checking a mock (Setup, Verify, Received, Returns) is not drawn as a call.</p>
          <p className="text-faint">Click a card for its file, a row for a member, or a line for where the calls are.</p>
        </div>
      </Section>
      {problems.length > 0 && (
        <Section title="Code that could not be read" count={problems.length}>
          {problems.map((f) => (
            <button key={f.path} onClick={() => openSourceFile(f.path)} className="flex w-full items-center gap-2 rounded-md px-1.5 py-1 text-left text-[12px] hover:bg-hover">
              <TriangleAlert size={13} className="flex-none text-changed" />
              <span className="min-w-0 flex-1 truncate">{f.path}</span>
              <span className="text-faint">{f.problems}</span>
            </button>
          ))}
        </Section>
      )}
    </>
  );
}

/** Each file reached, with how many of its members (and types created) are reached. */
function useReachedFiles(reach: Reach | null, index: CodeIndex): Map<string, number> {
  return useMemo(() => {
    const counts = new Map<string, number>((reach?.files ?? []).map((f) => [f, 0]));
    for (const id of [...(reach?.depth.keys() ?? []), ...(reach?.types ?? [])]) {
      const file = index.fileOf(id);
      if (file) counts.set(file, (counts.get(file) ?? 0) + 1);
    }
    return counts;
  }, [reach, index]);
}

function DataList({ data, index, testId }: { data: string[]; index: CodeIndex; testId: string }) {
  if (!data.length) return <Empty>No EF Core entity reached through a DbSet.</Empty>;
  return (
    <div className="-mx-1.5" data-testid={testId}>
      {data.map((id) => {
        const t = index.types.get(id);
        return (
          <button key={id} type="button" onClick={() => t?.parts[0] && openSourceFile(t.parts[0].file, t.parts[0].line)} className="flex w-full items-center gap-2 rounded-md px-1.5 py-1 text-left text-[12.5px] hover:bg-hover">
            <Database size={13} className="flex-none text-changed" />
            <span className="min-w-0 flex-1 truncate">{t?.name ?? id}</span>
            <span className="flex-none text-[11px] text-faint">through its DbSet</span>
          </button>
        );
      })}
    </div>
  );
}

function EndpointSummary({ endpoint, index }: { endpoint: CodeEndpoint; index: CodeIndex }) {
  const guesses = useCodeSettings((s) => s.guesses);
  const reach = useMemo(() => endpointReach(index.map, endpoint.id, { guesses }), [index, endpoint, guesses]);
  const tested = useMemo(() => (index.map.tests.length ? (endpointTestedBy(index.map, { guesses }).get(endpoint.id) ?? []) : null), [index, endpoint, guesses]);
  const files = useReachedFiles(reach, index);
  if (!reach) return null;
  const handler = endpoint.handler ? index.members.get(endpoint.handler) : undefined;
  return (
    <>
      <header className="space-y-1.5 border-b border-border px-3 pt-3 pb-3">
        <p className="text-[11px] font-semibold tracking-wider text-faint uppercase">{endpoint.kind === 'controller' ? 'Controller endpoint' : 'Minimal API endpoint'}</p>
        <h2 className="flex flex-wrap items-center gap-1.5 font-mono text-[13px] font-semibold break-all" data-testid="endpoint-title">
          <MethodBadges endpoint={endpoint} /> {endpoint.route}
        </h2>
        <Field label="Handler">{handler ? <span className="font-mono text-[12px]">{memberLabel(handler, index)}</span> : 'a lambda'}</Field>
        <Field label="Reaches">
          {reach.depth.size} members in {files.size} files
        </Field>
        <SourceLink file={endpoint.file} line={endpoint.line} />
        {reach.truncated && <p className="text-[12px] text-changed">Stopped after {reach.depth.size} members: it reaches more.</p>}
      </header>
      <Section title="Data" count={reach.data.length}>
        <DataList data={reach.data} index={index} testId="endpoint-data" />
      </Section>
      <Section title="Leaves the code" count={reach.handoffs.length}>
        <HandoffList handoffs={reach.handoffs} index={index} testId="endpoint-handoffs" />
      </Section>
      {tested && (
        <Section title="Tested by" count={tested.length}>
          <div data-testid="endpoint-tested-by">
            <TestedByList reaching={tested} index={index} />
          </div>
          <p className="mt-1.5 text-[12px] leading-relaxed text-faint">Tests sending it an HTTP request its method and route match, and tests calling its handler.</p>
        </Section>
      )}
      <Section title="Files" count={files.size}>
        <FileList counts={files} empty="Reaches nothing else in this folder." />
      </Section>
      <Section title="How it is followed" defaultOpen={false}>
        <div className="space-y-2 text-[12px] leading-relaxed text-muted">
          <p>From the handler, every call the code map found, step by step. A call to an interface or virtual member goes on to each implementation in the folder (dashed lines).</p>
          <p>Where a value of the code&apos;s types is handed to the framework or a package (Leaves the code), the reach stops, as the code shows nothing more. When the code has a probable handler of that type (a method taking it, in a class implementing a package type made for it, like a mediator&apos;s request handler), the reach goes on to it as a guess.</p>
          <p>Reading a property follows its getter when it has code of its own; plain auto-properties and fields are left out. {guesses ? 'Guessed calls are followed too.' : 'Guessed calls are not followed.'}</p>
        </div>
      </Section>
    </>
  );
}

function TestSummary({ test, index }: { test: CodeTest; index: CodeIndex }) {
  const guesses = useCodeSettings((s) => s.guesses);
  const reach = useMemo(() => testReach(index.map, test.id, { guesses }), [index, test, guesses]);
  const files = useReachedFiles(reach, index);
  // The requests the test and what it reaches send, with or without an endpoint of the code.
  const requests = useMemo(() => (reach ? index.map.requests.filter((r) => !r.endpoint && reach.depth.has(r.from)) : []), [reach, index]);
  if (!reach) return null;
  const folder = index.map.files.find((f) => f.path === test.file)?.project ?? null;
  const project = index.map.projects.find((p) => p.folder === folder);
  const setup = test.setup.flatMap((id) => index.members.get(id) ?? []);
  return (
    <>
      <header className="space-y-1.5 border-b border-border px-3 pt-3 pb-3">
        <p className="text-[11px] font-semibold tracking-wider text-faint uppercase">{FRAMEWORK_NAMES[test.framework]} test</p>
        <h2 className="font-mono text-[13px] font-semibold [overflow-wrap:anywhere]" data-testid="test-title">
          {test.name}
        </h2>
        <Field label="Class">
          <Mono>{typeName(test.type)}</Mono>
        </Field>
        <Field label="Kind">
          {testKind(test)}
          {test.skipped && <span className="text-changed">, skipped</span>}
        </Field>
        {project && (
          <Field label="Project">
            {project.name}
            {project.why && <span className="text-faint"> · {project.why}</span>}
          </Field>
        )}
        <Field label="Reaches">
          {reach.depth.size} members in {files.size} files
        </Field>
        <SourceLink file={test.file} line={test.line} />
        {reach.truncated && <p className="text-[12px] text-changed">Stopped after {reach.depth.size} members: it reaches more.</p>}
      </header>
      <Section title="Runs with it" count={setup.length}>
        {setup.length ? (
          <div className="-mx-1.5" data-testid="test-setup">
            {setup.map((m) => (
              <button key={m.id} type="button" onClick={() => selectCode({ kind: 'member', id: m.id })} className="flex w-full items-center gap-2 rounded-md px-1.5 py-1 text-left text-[12.5px] hover:bg-hover" title={m.id}>
                <ArrowRight size={13} className="flex-none text-faint" />
                <span className="min-w-0 flex-1 truncate font-mono text-[12px]">{memberLabel(m, index)}</span>
                <span className="flex-none text-[11px] text-faint">{KIND[m.kind].toLowerCase()}</span>
              </button>
            ))}
          </div>
        ) : (
          <Empty>Nothing else: no constructor, field initializer, set-up or tear-down.</Empty>
        )}
      </Section>
      <Section title="Fakes" count={reach.fakes.length}>
        {reach.fakes.length ? (
          <div className="-mx-1.5" data-testid="test-fakes">
            {reach.fakes.map((f) => {
              const t = index.types.get(f.type);
              return (
                <button key={f.type} type="button" onClick={() => t?.parts[0] && revealCode(t.parts[0].file)} className="flex w-full items-center gap-2 rounded-md px-1.5 py-1 text-left text-[12.5px] hover:bg-hover" title={f.type}>
                  <VenetianMask size={13} className="flex-none text-muted" />
                  <span className="min-w-0 flex-1 truncate">{t?.name ?? typeName(f.type)}</span>
                  <span className="flex-none text-[11px] text-faint">{f.by ? `by ${typeName(f.by)}` : 'mocked'}</span>
                </button>
              );
            })}
          </div>
        ) : (
          <Empty>Fakes nothing: every call goes to the code itself.</Empty>
        )}
      </Section>
      <Section title="Data" count={reach.data.length}>
        <DataList data={reach.data} index={index} testId="test-data" />
      </Section>
      <Section title="HTTP requests" count={requests.length}>
        <RequestList requests={requests} testId="test-requests" />
      </Section>
      <Section title="Leaves the code" count={reach.handoffs.length} defaultOpen={false}>
        <HandoffList handoffs={reach.handoffs} index={index} testId="test-handoffs" />
      </Section>
      <Section title="Files" count={files.size}>
        <FileList counts={files} empty="Reaches nothing else in this folder." />
      </Section>
      <Section title="How it is followed" defaultOpen={false}>
        <div className="space-y-2 text-[12px] leading-relaxed text-muted">
          <p>From the test method and what the framework runs with it (the class&apos;s constructors and field initializers, set-up and tear-down methods, xunit fixtures), every call the code map found, step by step. A call to an interface or virtual member goes on to each implementation in the folder (dashed lines).</p>
          <p>A type the test fakes is not followed into its real code: mocked with Moq, NSubstitute or FakeItEasy, or implemented by a test class the test creates, which is followed instead. Setting up or checking a mock (Setup, Verify, Received, Returns) does not count as a call.</p>
          <p>An HTTP request the test sends (with an HttpClient, often the test host&apos;s) goes into the endpoint of the code its method and route match: its handler, or its handler lambda. Dashed when the match is a guess (the URL&apos;s open parts stand for route text, or several routes fit).</p>
          <p>Where a value is handed to the framework or a package (Leaves the code), the reach goes on only to the code&apos;s probable handler of it, as a guess.</p>
          <p>This is what the test can reach by reading the code, not what it covers when it runs. {guesses ? 'Guessed calls are followed too.' : 'Guessed calls are not followed.'}</p>
        </div>
      </Section>
    </>
  );
}

export function CodeInspector() {
  const map = useCodeMap((s) => s.map);
  const loading = useCodeMap((s) => s.loading);
  const selection = useCodeMap((s) => s.selection);
  const view = useCodeMap((s) => s.view);
  const endpoint = useCurrentEndpoint();
  const test = useCurrentTest();
  if (!map) return <p className="p-4 text-[12.5px] text-faint">{loading ? `Reading code… ${loading.read} of ${loading.total} files` : 'Reading code…'}</p>;
  const index = codeIndex(map);
  let body: React.ReactNode;
  if (selection?.kind === 'member') {
    const member = index.members.get(selection.id);
    body = member ? <MemberDetails member={member} index={index} /> : <Summary map={map} />;
  } else if (selection?.kind === 'file') body = <FileDetails path={selection.path} index={index} />;
  else if (selection?.kind === 'edge') body = <EdgeDetails id={selection.id} index={index} />;
  else if (view === 'endpoints' && endpoint) body = <EndpointSummary endpoint={endpoint} index={index} />;
  else if (view === 'tests' && test) body = <TestSummary test={test} index={index} />;
  else body = <Summary map={map} />;
  return (
    <aside className="h-full overflow-y-auto" aria-label="Inspector">
      {selection && (
        <button type="button" onClick={() => selectCode(null)} className="flex h-8 w-full items-center gap-1 border-b border-border px-3 text-[12px] text-muted hover:text-text">
          ← {view === 'endpoints' && endpoint ? endpointLabel(endpoint) : view === 'tests' && test ? test.name : 'Code map'}
        </button>
      )}
      {body}
    </aside>
  );
}
